import { useState, useEffect, useDeferredValue } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";

interface CompanyFilterBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  companyTypeFilter: string;
  setCompanyTypeFilter: (type: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  tradeSpecialtiesFilter: string;
  setTradeSpecialtiesFilter: (specialty: string) => void;
  tradeSpecialties: Array<{ id: string; name: string; }>;
  prequalificationStatusFilter: string;
  setPrequalificationStatusFilter: (status: string) => void;
}

export function CompanyFilterBar({
  searchQuery,
  setSearchQuery,
  companyTypeFilter,
  setCompanyTypeFilter,
  statusFilter,
  setStatusFilter,
  tradeSpecialtiesFilter,
  setTradeSpecialtiesFilter,
  tradeSpecialties,
  prequalificationStatusFilter,
  setPrequalificationStatusFilter,
}: CompanyFilterBarProps) {
  // Local state for immediate input response
  const [localSearch, setLocalSearch] = useState(searchQuery);
  
  // Debounced value for actual filtering
  const deferredSearch = useDeferredValue(localSearch);

  // Sync debounced value to parent
  useEffect(() => {
    setSearchQuery(deferredSearch);
  }, [deferredSearch, setSearchQuery]);

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          placeholder="Search partners..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      <Select value={companyTypeFilter} onValueChange={setCompanyTypeFilter}>
        <SelectTrigger className="w-full sm:w-[180px]">
          <SelectValue placeholder="Partner Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          <SelectItem value="client">Client</SelectItem>
          <SelectItem value="prospect">Prospect</SelectItem>
          <SelectItem value="vendor">Vendor</SelectItem>
          <SelectItem value="contractor">Contractor</SelectItem>
          <SelectItem value="partner">Partner</SelectItem>
        </SelectContent>
      </Select>

      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-full sm:w-[180px]">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          <SelectItem value="active">Active</SelectItem>
          <SelectItem value="inactive">Inactive</SelectItem>
          <SelectItem value="prospect">Prospect</SelectItem>
          <SelectItem value="archived">Archived</SelectItem>
        </SelectContent>
      </Select>

      <Select value={tradeSpecialtiesFilter} onValueChange={setTradeSpecialtiesFilter}>
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue placeholder="Trade Specialty" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Specialties</SelectItem>
          {tradeSpecialties.map((specialty) => (
            <SelectItem key={specialty.id} value={specialty.id}>
              {specialty.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={prequalificationStatusFilter} onValueChange={setPrequalificationStatusFilter}>
        <SelectTrigger className="w-full sm:w-[200px]">
          <SelectValue placeholder="Prequalification" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Prequalification</SelectItem>
          <SelectItem value="not_started">Not Started</SelectItem>
          <SelectItem value="in_progress">In Progress</SelectItem>
          <SelectItem value="pending_review">Pending Review</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
          <SelectItem value="expired">Expired</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
